import styled from "styled-components";
import { tablet } from "styles/breakpoints";

// Color schemes for each category
export interface CategoryColorScheme {
  border: string;
  iconBackground: string;
  iconColor: string;
  /** Solid background for the expanded header */
  headerBackground: string;
}

export const colorPalette: CategoryColorScheme[] = [
  { border: "#EC407A", iconBackground: "rgba(236, 64, 122, 0.12)", iconColor: "#EC407A", headerBackground: "linear-gradient(135deg, #EC407A 0%, #AD1457 100%)" },
  { border: "#7E57C2", iconBackground: "rgba(126, 87, 194, 0.12)", iconColor: "#7E57C2", headerBackground: "linear-gradient(135deg, #7E57C2 0%, #512DA8 100%)" },
  { border: "#4CAF50", iconBackground: "rgba(76, 175, 80, 0.12)", iconColor: "#4CAF50", headerBackground: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)" },
  { border: "#42A5F5", iconBackground: "rgba(66, 165, 245, 0.12)", iconColor: "#42A5F5", headerBackground: "linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)" },
  { border: "#C62828", iconBackground: "rgba(198, 40, 40, 0.12)", iconColor: "#C62828", headerBackground: "linear-gradient(135deg, #C62828 0%, #8E0000 100%)" },
  { border: "#3949AB", iconBackground: "rgba(57, 73, 171, 0.12)", iconColor: "#3949AB", headerBackground: "linear-gradient(135deg, #3949AB 0%, #1A237E 100%)" },
  { border: "#9C27B0", iconBackground: "rgba(156, 39, 176, 0.12)", iconColor: "#9C27B0", headerBackground: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)" },
  { border: "#FFA726", iconBackground: "rgba(255, 167, 38, 0.12)", iconColor: "#FFA726", headerBackground: "linear-gradient(135deg, #FFA726 0%, #EF6C00 100%)" },
  { border: "#26A69A", iconBackground: "rgba(38, 166, 154, 0.12)", iconColor: "#26A69A", headerBackground: "linear-gradient(135deg, #26A69A 0%, #00796B 100%)" },
  { border: "#78909C", iconBackground: "rgba(120, 144, 156, 0.12)", iconColor: "#78909C", headerBackground: "linear-gradient(135deg, #78909C 0%, #546E7A 100%)" },
];

export function getColorByIndex(index: number): CategoryColorScheme {
  return colorPalette[index % colorPalette.length];
}

export const CardWrapper = styled.div<{
  $colors: CategoryColorScheme;
  $isViewed: boolean;
  $isExpanded: boolean;
}>`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: ${({ $isExpanded }) =>
    $isExpanded
      ? "0 2px 12px rgba(0, 0, 0, 0.12)"
      : "0 1px 4px rgba(0, 0, 0, 0.08)"};
  border: ${({ $isViewed, $isExpanded, $colors }) =>
    $isExpanded
      ? "none"
      : $isViewed
        ? `1px solid ${$colors.border}`
        : "none"};
  border-left: ${({ $isExpanded, $colors }) =>
    $isExpanded
      ? "none"
      : `4px solid ${$colors.border}`};
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
`;

export const TileHeader = styled.div<{
  $isExpanded: boolean;
  $headerBackground: string;
}>`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;
  gap: 12px;
  transition: background 0.15s ease;
  background: ${({ $isExpanded, $headerBackground }) =>
    $isExpanded ? $headerBackground : "transparent"};
  color: ${({ $isExpanded }) => ($isExpanded ? "#ffffff" : "inherit")};

  &:hover {
    background: ${({ $isExpanded, $headerBackground }) =>
      $isExpanded ? $headerBackground : "rgba(0, 0, 0, 0.02)"};
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(121, 142, 207, 0.4);
  }
`;

export const IconWrapper = styled.div<{
  $background: string;
  $color: string;
  $isExpanded: boolean;
}>`
  width: 44px;
  height: 44px;
  background: ${({ $isExpanded, $background }) =>
    $isExpanded ? "rgba(255, 255, 255, 0.2)" : $background};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $isExpanded, $color }) =>
    $isExpanded ? "#ffffff" : $color};

  svg,
  img {
    width: 22px;
    height: 22px;
    object-fit: contain;
  }

  img {
    filter: ${({ $isExpanded }) =>
      $isExpanded ? "none" : "brightness(0)"};
    transition: filter 0.2s ease;
  }
`;

export const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 4px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const TileTitle = styled.span<{ $isExpanded: boolean }>`
  font-size: 15px;
  font-weight: 700;
  color: ${({ $isExpanded }) => ($isExpanded ? "#ffffff" : "#4a4d5e")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RiskBadge = styled.span<{ $isHigh: boolean; $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  background: ${({ $isHigh, $isExpanded }) =>
    $isExpanded
      ? "rgba(255, 255, 255, 0.2)"
      : $isHigh
        ? "rgba(177, 2, 22, 0.1)"
        : "rgba(74, 140, 59, 0.1)"};
  color: ${({ $isHigh, $isExpanded }) =>
    $isExpanded ? "#ffffff" : $isHigh ? "#B10216" : "#4A8C3B"};
`;

export const ActionBadge = styled.span<{ $isHigh: boolean; $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  background: ${({ $isHigh, $isExpanded }) =>
    $isExpanded
      ? "rgba(255, 255, 255, 0.2)"
      : $isHigh
        ? "rgba(255, 152, 0, 0.12)"
        : "rgba(74, 140, 59, 0.1)"};
  color: ${({ $isHigh, $isExpanded }) =>
    $isExpanded ? "#ffffff" : $isHigh ? "#E65100" : "#4A8C3B"};

  svg {
    width: 10px;
    height: 10px;
  }
`;

export const ShortText = styled.span<{ $isExpanded: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $isExpanded }) => ($isExpanded ? "rgba(255, 255, 255, 0.85)" : "#6b6b76")};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActionsArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const SelectionToggle = styled.div<{
  $checked: boolean;
  $color: string;
  $isExpanded: boolean;
}>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid
    ${({ $checked, $color, $isExpanded }) =>
      $isExpanded
        ? $checked
          ? "#ffffff"
          : "rgba(255, 255, 255, 0.5)"
        : $checked
          ? $color
          : "#C4C4C4"};
  background: ${({ $checked, $color, $isExpanded }) =>
    $isExpanded
      ? $checked
        ? "#ffffff"
        : "transparent"
      : $checked
        ? $color
        : "transparent"};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    color: ${({ $isExpanded, $color }) =>
      $isExpanded ? $color : "#ffffff"};
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
  }

  &:hover {
    border-color: ${({ $color }) => $color};
    background: ${({ $checked, $color }) =>
      $checked ? $color : "rgba(121, 142, 207, 0.1)"};
  }
`;

export const ExpandChevron = styled.div<{ $isExpanded: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isExpanded }) => ($isExpanded ? "rgba(255, 255, 255, 0.7)" : "#9e9e9e")};
  flex-shrink: 0;
  transition: transform 0.2s ease, color 0.2s ease;
  transform: ${({ $isExpanded }) => ($isExpanded ? "rotate(180deg)" : "rotate(0deg)")};

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Expanded content area
export const ExpandedContent = styled.div`
  padding: 0 16px 20px;
  border-top: 1px solid #f0f0f0;

  @media (min-width: ${tablet}px) {
    padding: 0 20px 24px;
  }
`;

export const NextStepsSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: #ffffff;
`;

export const NextStepsTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #4a4d5e;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const NextStepsIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: #FFA726;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const NextStepsBody = styled.div`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: #6b6b76;

  p {
    margin: 0 0 8px 0;
    &:last-child { margin-bottom: 0; }
  }

  a {
    color: #798ecf;
    text-decoration: underline;
    &:hover { color: #5a6fb0; }
  }

  strong, b { font-weight: 600; }
`;

export const ContentItemsArea = styled.div`
  padding: 16px 0;
`;

export const VideosArea = styled.div`
  padding: 8px 0 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: ${tablet}px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }
`;

export const VideoCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;

  video {
    width: 100%;
    display: block;
  }
`;

export const VideoCardTitle = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #4a4d5e;
  background: #f9f9fb;
  border-top: 1px solid #e0e0e0;
`;

export const LearnMoreToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #696E8C;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const LearnMoreText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #696E8C;
`;

export const LearnMoreIcon = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: #696E8C;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => ($expanded ? "rotate(180deg)" : "rotate(0deg)")};

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const LearnMoreContent = styled.div<{ $expanded: boolean }>`
  display: ${({ $expanded }) => ($expanded ? "block" : "none")};
  padding-top: 12px;
`;

export const RiskFactorsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: ${tablet}px) {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
`;

export const RiskFactorBox = styled.div<{
  $type: "increasing" | "decreasing";
}>`
  border-radius: 8px;
  padding: 14px;

  background-color: ${({ $type }) =>
    $type === "increasing" ? "#F6EEEF" : "#F1F4F0"};
`;

export const RiskFactorHeader = styled.h4<{
  $type: "increasing" | "decreasing";
}>`
  margin: 0 0 10px 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: ${({ $type }) =>
    $type === "increasing" ? "#B10216" : "#4A8C3B"};
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const RiskFactorIcon = styled.span<{
  $type: "increasing" | "decreasing";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: ${({ $type }) =>
    $type === "increasing" ? "#B10216" : "#4A8C3B"};

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const RiskFactorList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

export const RiskFactorItem = styled.li<{
  $type: "increasing" | "decreasing";
}>`
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  padding-left: 18px;
  margin-bottom: 6px;
  color: #6b6b76;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({ $type }) =>
      $type === "increasing" ? "#B10216" : "#4A8C3B"};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const BodyHtmlSection = styled.div`
  padding: 0 4px;

  p {
    margin: 0 0 10px 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: #4a4d5e;
    &:last-child { margin-bottom: 0; }
  }

  ul, ol {
    margin: 0 0 10px 0;
    padding-left: 20px;
    &:last-child { margin-bottom: 0; }
  }

  li {
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: #4a4d5e;
  }

  a {
    color: #798ecf;
    text-decoration: underline;
    &:hover { color: #5a6fb0; }
  }

  strong, b { font-weight: 600; }
`;

export const InterestIndicatorRow = styled.div<{
  $color: string;
  $checked: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 12px 14px;
  border: 1px solid ${({ $checked, $color }) => ($checked ? $color : "#e8e8e8")};
  border-radius: 12px;
  color: ${({ $color }) => $color};
  cursor: pointer;
  user-select: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    background: ${({ $color }) => `${$color}08`};
  }
`;

export const InterestIndicatorCheck = styled.span<{
  $color: string;
  $checked: boolean;
}>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  color: ${({ $checked, $color }) => ($checked ? $color : "#c0c0c8")};
  transition: color 0.2s ease;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const InterestIndicatorIcon = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-left: auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: none;
  }
`;

export const InterestIndicatorLabel = styled.span<{ $checked: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $checked }) => ($checked ? "#1a1a2e" : "#9999a6")};
  flex: 1;
  transition: color 0.2s ease;
`;
